import { ChevronRight, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import styles from './List.module.css';

/**
 * Rows separated by hairlines, as in system settings. Inside a flush Section it fills the
 * card; `standalone` draws its own white rounded card.
 */
export function List({
  children,
  standalone,
  divided,
  label,
}: {
  children: ReactNode;
  standalone?: boolean;
  /** A hairline above the list (a second list in the same card). */
  divided?: boolean;
  label?: string;
}) {
  const className = [styles.list, standalone ? styles.standalone : '', divided ? styles.divided : ''].filter(Boolean).join(' ');
  return (
    <ul className={className} aria-label={label}>
      {children}
    </ul>
  );
}

/** A fact: small label above its value, with an icon in front. */
export function InfoRow({ icon: Icon, label, children }: { icon: LucideIcon; label: string; children: ReactNode }) {
  return (
    <li className={`${styles.row} ${styles.info}`}>
      <Icon aria-hidden="true" className={styles.icon} />
      <div className={styles.text}>
        <span className={styles.label}>{label}</span>
        <div className={styles.value}>{children}</div>
      </div>
    </li>
  );
}

interface ActionRowProps {
  icon: LucideIcon;
  children: ReactNode;
  /** Secondary line under the label. */
  detail?: ReactNode;
  to?: string;
  href?: string;
  onClick?: () => void;
  disabled?: boolean;
  tone?: 'danger';
}

/** A tappable row: opens a screen (chevron) or runs an action. */
export function ActionRow({ icon: Icon, children, detail, to, href, onClick, disabled, tone }: ActionRowProps) {
  const className = [styles.row, styles.action, tone === 'danger' ? styles.danger : ''].filter(Boolean).join(' ');
  const content = (
    <>
      <Icon aria-hidden="true" className={styles.icon} />
      <span className={styles.text}>
        <span className={styles.actionLabel}>{children}</span>
        {detail && <span className={styles.detail}>{detail}</span>}
      </span>
      {to && <ChevronRight aria-hidden="true" className={styles.chevron} />}
    </>
  );
  return (
    <li>
      {to ? (
        <Link to={to} className={className}>
          {content}
        </Link>
      ) : href ? (
        <a href={href} className={className}>
          {content}
        </a>
      ) : (
        <button type="button" className={className} onClick={onClick} disabled={disabled}>
          {content}
        </button>
      )}
    </li>
  );
}
